import type { Form20000Response, Form20001Response, TaxpayerMeta } from "../../types/api";
import { calculateSocialTaxBase } from "../payrollBase";
import {
  num,
  quarterColumn,
  emptyQuarterColumn,
  emptyMonthTriad,
  type IsnaNumber,
} from "./helpers";

export interface BuildFno200Input {
  form200: Form20000Response;
  form20001: Form20001Response;
  meta: TaxpayerMeta;
  year: number;
  quarter: number;
  declarationDate: string;
}

function buildCalculatedIndicators(form200: Form20000Response) {
  const m1 = form200.month1;
  const m2 = form200.month2;
  const m3 = form200.month3;
  const total = form200.quarter_total;
  return {
    _001: quarterColumn(m1.ipn, m2.ipn, m3.ipn, total.ipn),
    _002: quarterColumn(m1.opv, m2.opv, m3.opv, total.opv),
    _003: emptyQuarterColumn(),
    _004: emptyQuarterColumn(),
    _005: quarterColumn(m1.sn_payable, m2.sn_payable, m3.sn_payable, total.sn_payable),
    _006: emptyQuarterColumn(),
    _007: emptyQuarterColumn(),
    _008: quarterColumn(m1.so, m2.so, m3.so, total.so),
    _009: emptyQuarterColumn(),
    _010: quarterColumn(m1.vosms_employer, m2.vosms_employer, m3.vosms_employer, total.vosms_employer),
    _011: quarterColumn(m1.vosms_employee, m2.vosms_employee, m3.vosms_employee, total.vosms_employee),
    _012: emptyQuarterColumn(),
    _013: quarterColumn(m1.opvr, m2.opvr, m3.opvr, total.opvr),
    _014: emptyQuarterColumn(),
    _015: emptyQuarterColumn(),
  };
}

function buildApplication01(form: Form20001Response) {
  const m1 = form.month1;
  const m2 = form.month2;
  const m3 = form.month3;
  const total = form.quarter_total;
  return {
    _001: {
      ...quarterColumn(m1.accrued_income, m2.accrued_income, m3.accrued_income, total.accrued_income),
      A: num(form.accrued_income_employees),
      B: null as IsnaNumber,
      C: null as IsnaNumber,
      D: null as IsnaNumber,
      E: null as IsnaNumber,
    },
    _002: emptyQuarterColumn(),
    _003: quarterColumn(m1.ipn, m2.ipn, m3.ipn, total.ipn),
    _004: null as IsnaNumber,
    _005: null as IsnaNumber,
    _006: null as IsnaNumber,
    _007: quarterColumn(m1.paid_income, m2.paid_income, m3.paid_income, total.paid_income),
    _008: quarterColumn(m1.taxable_income, m2.taxable_income, m3.taxable_income, total.taxable_income),
    _009: quarterColumn(m1.accrued_income, m2.accrued_income, m3.accrued_income, total.accrued_income),
    _010: emptyQuarterColumn(),
    _011: quarterColumn(m1.opvr_base, m2.opvr_base, m3.opvr_base, total.opvr_base),
    _012: emptyQuarterColumn(),
    _013: emptyMonthTriad(),
    _014: emptyMonthTriad(),
    _015: emptyMonthTriad(),
    _016: quarterColumn(m1.sn_base, m2.sn_base, m3.sn_base, total.sn_base),
    _017: quarterColumn(
      calculateSocialTaxBase(m1.accrued_income, m1.opv),
      calculateSocialTaxBase(m2.accrued_income, m2.opv),
      calculateSocialTaxBase(m3.accrued_income, m3.opv),
      calculateSocialTaxBase(total.accrued_income, total.opv),
    ),
    _018: quarterColumn(m1.so_self_base, m2.so_self_base, m3.so_self_base, total.so_self_base),
    _019: emptyQuarterColumn(),
    _020: quarterColumn(m1.vosms_base, m2.vosms_base, m3.vosms_base, total.vosms_base),
    _021: emptyQuarterColumn(),
  };
}

export function buildFno200(input: BuildFno200Input) {
  const { form200, form20001, meta, year, quarter, declarationDate } = input;
  const bin = form200.organization_bin;
  const name = meta.taxpayer_name_full;
  const type = meta.taxpayer_type;

  return {
    periodType: "QUARTER",
    periodValue: quarter,
    fnoYear: year,
    taxpayerType: type,
    taxpayerCode: bin,
    taxpayerNameRu: name,
    taxpayerNameKk: name,
    taxpayerNameEn: name,
    taxpayerNameQq: name,
    taxpayerCategories: [],
    creatorType: type,
    creatorCode: bin,
    creatorNameRu: name,
    creatorNameKk: name,
    creatorNameEn: name,
    creatorNameQq: name,
    declarationTypes: ["NEXT_PRIORITY"],
    ogdCodeByLocation: null,
    ogdCodeByResidence: null,
    currencyCode: "KZT",
    noticeNumber: "",
    noticeDate: null,
    fnoContent: {
      type: "fno200",
      taxpayerResponsibility: {
        fullNameOfHead: meta.chief_name,
        isResponsible: true,
        declarationDate,
        fullNameOfAuthorizedPerson: null,
        fullNameOfEmployeeWhoAcceptedDeclaration: null,
        declarationReceiptDate: null,
        declarationIncomingNumber: null,
        postmarkDate: null,
        beneficiaryOfIPNAndSocialTaxCode: null,
        beneficiaryOfSocialPaymentsCode: null,
      },
      commonInfo: {
        _7: false,
        _9: "A",
        _10: {
          _1: form200.month1.employee_count || null,
          _2: form200.month2.employee_count || null,
          _3: form200.month3.employee_count || null,
          _4: null,
          _5: null,
          _6: null,
        },
        _11: false,
        selectedApplications: ["application_01"],
        _13: null,
        _14: null,
        _15: null,
      },
      calculatedIndicators: buildCalculatedIndicators(form200),
      application_01: buildApplication01(form20001),
      application_02: [],
      application_03: [],
      application_04: [],
      application_05: [],
      application_06: [],
    },
  };
}
